/**
 * Central company resolver + React context.
 *
 * Selection priority (first match wins):
 *   1. Server-side resolution via `GET /api/tenant` using the active Host header
 *      — this is the authoritative source in multi-tenant deployments.
 *   2. REACT_APP_COMPANY env var                — e.g. REACT_APP_COMPANY=gtes
 *   3. Hostname / domain match from bundled configs
 *   4. Fallback DEFAULT_SLUG                    — gtes
 *
 * To add a new company:
 *   • copy ./companies/company-template.js → ./companies/<slug>.js
 *   • fill in values + add logo under src/assets/logos/<slug>/
 *   • register the import in COMPANIES below
 *   • register its hostnames in backend/tenants.py HOST_TO_TENANT
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { assertCompanyShape } from "./schema";
import gtes from "./companies/gtes";
import petrophoenix from "./companies/petrophoenix";

const DEFAULT_SLUG = "gtes";
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/** Registry — add new companies here. Each value is validated at import time. */
export const COMPANIES = {
  gtes: assertCompanyShape(gtes, "companies/gtes.js"),
  petrophoenix: assertCompanyShape(petrophoenix, "companies/petrophoenix.js"),
};

/** Normalize a server tenant payload (snake_case) to the frontend schema (camelCase). */
function normalizeServerTenant(t) {
  if (!t || !t.slug) return null;
  const bundled = COMPANIES[t.slug] || {};
  // Prefer server values, fall back to bundled defaults for anything the API omits.
  const resolveLogo = (url, fallback) => {
    if (!url) return fallback;
    if (/^https?:\/\//i.test(url)) return url;
    // Server returns relative paths like `/tenant-assets/gtes/logo.jpg`
    return API_BASE + url;
  };
  return {
    slug: t.slug,
    name: t.name || bundled.name,
    shortName: t.short_name || bundled.shortName,
    tagline: t.tagline || bundled.tagline,
    website: t.website || bundled.website,
    domains: bundled.domains || [],
    brand: {
      primaryColor: (t.brand && t.brand.primary) || bundled.brand.primaryColor,
      secondaryColor: (t.brand && t.brand.secondary) || bundled.brand.secondaryColor,
      accentColor: (t.brand && t.brand.accent) || bundled.brand.accentColor,
      logo: resolveLogo(t.logo_url, bundled.brand.logo),
      favicon: resolveLogo(t.favicon_url, bundled.brand.favicon),
    },
    contact: t.contact || bundled.contact,
    social: t.social || bundled.social,
    hero: t.hero
      ? {
          title: t.hero.title,
          description: t.hero.description,
          primaryCta: t.hero.primary_cta,
          secondaryCta: t.hero.secondary_cta,
        }
      : bundled.hero,
    about: t.about || bundled.about,
    services: t.services || bundled.services,
    testimonials: t.testimonials || bundled.testimonials,
    portfolio: t.portfolio || bundled.portfolio,
    seo: t.seo || bundled.seo,
    extras: {
      ...(bundled.extras || {}),
      footerCopyright: (t.extras && t.extras.footer_copyright) || (bundled.extras && bundled.extras.footerCopyright),
    },
  };
}

/** Match a hostname against every bundled company's `domains` list. */
function matchByHostname(hostname) {
  if (!hostname) return null;
  const host = hostname.toLowerCase();
  for (const slug of Object.keys(COMPANIES)) {
    const cfg = COMPANIES[slug];
    if ((cfg.domains || []).some((d) => d.toLowerCase() === host)) return slug;
  }
  return null;
}

/** Resolve the active company at startup from local hints only (synchronous). */
export function resolveCompany() {
  const envSlug = (process.env.REACT_APP_COMPANY || "").trim().toLowerCase();
  if (envSlug && COMPANIES[envSlug]) return COMPANIES[envSlug];
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const matched = matchByHostname(host);
    if (matched) return COMPANIES[matched];
  }
  return COMPANIES[DEFAULT_SLUG];
}

/** Fetch the server-resolved tenant for the current Host. Returns null on failure. */
async function fetchServerTenant() {
  if (!API_BASE) return null;
  try {
    const r = await fetch(`${API_BASE}/api/tenant`, { credentials: "include" });
    if (!r.ok) return null;
    const data = await r.json();
    return normalizeServerTenant(data);
  } catch {
    return null;
  }
}

const CompanyContext = createContext(null);

/** Apply brand colors as CSS custom properties + update <title> + favicon. */
function applyBrandSideEffects(cfg) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", cfg.brand.primaryColor);
  root.style.setProperty("--brand-secondary", cfg.brand.secondaryColor);
  root.style.setProperty("--brand-accent", cfg.brand.accentColor || cfg.brand.primaryColor);
  document.title = (cfg.seo && cfg.seo.title) || `${cfg.shortName} — ${cfg.tagline}`;
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = cfg.brand.favicon;
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", (cfg.seo && cfg.seo.description) || cfg.tagline);
}

export function CompanyProvider({ children, override }) {
  // Start with the synchronous best-guess (env var / bundled hostname match)
  // so the UI never flashes a completely blank slate. The authoritative value
  // from /api/tenant replaces this as soon as the network responds.
  const [company, setCompany] = useState(() => override || resolveCompany());
  useEffect(() => {
    if (override) return;
    let cancelled = false;
    fetchServerTenant().then((srv) => {
      if (!cancelled && srv) setCompany(srv);
    });
    return () => { cancelled = true; };
  }, [override]);
  useEffect(() => { applyBrandSideEffects(company); }, [company]);
  return <CompanyContext.Provider value={company}>{children}</CompanyContext.Provider>;
}

/** Read the active company config from any component. */
export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    // Allow module-level use (e.g., in default props) by falling back to the resolver.
    return resolveCompany();
  }
  return ctx;
}

/** Convenience: `withYear("© {YEAR} ACME")` → `"© 2026 ACME"` */
export function withYear(str) {
  return (str || "").replace("{YEAR}", String(new Date().getFullYear()));
}
